import React, { useState } from 'react';
import { ArrowRight, KeyRound, LockKeyhole, ShieldCheck } from 'lucide-react';
import { api } from '../api.js';
import { Button, Callout } from '../components/ui.jsx';

export default function AuthPage({ setupRequired = false, mustChange = false, user = null, onAuthenticated }) {
  const [form, setForm] = useState({ email: user?.email || '', currentPassword: '', password: '', confirmPassword: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    if ((setupRequired || mustChange) && form.password !== form.confirmPassword) {
      setError('两次输入的新密码不一致。');
      return;
    }
    setBusy(true);
    try {
      let result;
      if (setupRequired) result = await api.setupAdmin({ email: form.email, password: form.password });
      else if (mustChange) {
        const nextUser = await api.changePassword({ currentPassword: form.currentPassword, newPassword: form.password });
        result = { authenticated: true, setupRequired: false, user: nextUser };
      } else result = await api.login({ email: form.email, password: form.password });
      await onAuthenticated(result);
    } catch (cause) {
      setError(cause.message);
    } finally {
      setBusy(false);
    }
  };

  const title = setupRequired ? '初始化管理员登录' : mustChange ? '设置你的新密码' : '登录招聘工作台';
  const description = setupRequired
    ? '首次使用需要由现有管理员设置登录密码，完成后所有页面和 API 都会启用会话校验。'
    : mustChange
      ? '管理员为你设置了临时密码。继续使用前，请先更换为只有你知道的新密码。'
      : '使用工作邮箱和密码继续。账户角色决定可执行操作，招聘官只能访问已授权负责部门。';

  return (
    <main className="auth-page">
      <section className="auth-brand-panel">
        <img className="auth-brand-logo" src="/brand/hinomi-logo-gold-horizontal.png" alt="Hinomi" />
        <span>Talent Intelligence</span>
        <h1>Evidence-led recruiting,<br />with accountable AI.</h1>
        <p>让模型分析、人工判断和组织权限保持清晰边界。</p>
        <div className="auth-trust-list">
          <div><ShieldCheck size={18} /><span><strong>角色权限</strong><small>管理员、人事与招聘官使用不同操作边界</small></span></div>
          <div><LockKeyhole size={18} /><span><strong>安全会话</strong><small>密码使用加盐密钥派生，登录会话仅存于 HttpOnly Cookie</small></span></div>
        </div>
      </section>
      <section className="auth-form-panel">
        <form className="auth-card" onSubmit={submit}>
          <div className="auth-card-icon"><KeyRound size={20} /></div>
          <header><span>{setupRequired ? 'FIRST-RUN SETUP' : mustChange ? 'PASSWORD UPDATE' : 'SECURE SIGN IN'}</span><h2>{title}</h2><p>{description}</p></header>
          {error && <Callout tone="danger" title="无法继续">{error}</Callout>}
          {!mustChange && <label><span>工作邮箱</span><input type="email" autoComplete="username" value={form.email} onChange={(event) => update('email', event.target.value)} placeholder="name@company.com" required /></label>}
          {mustChange && <label><span>当前密码</span><input type="password" autoComplete="current-password" value={form.currentPassword} onChange={(event) => update('currentPassword', event.target.value)} required /></label>}
          <label><span>{setupRequired || mustChange ? '新密码' : '密码'}</span><input type="password" autoComplete={setupRequired || mustChange ? 'new-password' : 'current-password'} value={form.password} onChange={(event) => update('password', event.target.value)} required /></label>
          {(setupRequired || mustChange) && <label><span>确认新密码</span><input type="password" autoComplete="new-password" value={form.confirmPassword} onChange={(event) => update('confirmPassword', event.target.value)} required /></label>}
          {(setupRequired || mustChange) && <small className="password-guidance">至少 10 个字符，同时包含字母和数字。</small>}
          <Button type="submit" variant="primary" icon={ArrowRight} loading={busy}>{setupRequired ? '完成初始化并登录' : mustChange ? '更新密码并继续' : '登录'}</Button>
          <footer>登录会话 12 小时后自动失效。停用账户或管理员重置密码会立即撤销已有会话。</footer>
        </form>
      </section>
    </main>
  );
}
